import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Collapsible-rail state, shared by the two left rails in the product: the
 * app shell's navigation and the RFQ wizard's journey rail. Either one can be
 * a slim icon rail that reveals its full self as a hover/focus flyout
 * (overlay — the page never reflows), or pinned back into flow as a real
 * column. Which shape it starts in is the caller's choice, and the user's
 * choice from then on, remembered per browser under the caller's own key.
 *
 * The two shells differ only in that default, on purpose:
 *   - app navigation opens (`SHELL_SIDEBAR_RAIL`) — a first-time visitor
 *     should see where the product's pages are, not five unlabelled icons.
 *   - the wizard journey starts minimized (`WIZARD_JOURNEY_RAIL`) — inside an
 *     RFQ the step content is the work, and the journey is orientation the
 *     user can call up when they want it.
 *
 * The two delays are the whole point of the hover behaviour: opening is
 * deliberate enough that a pointer crossing the rail on its way elsewhere does
 * not flash the panel, and closing is slow enough that the diagonal from the
 * rail into the flyout does not drop it mid-move.
 */
const OPEN_DELAY_MS = 120;
const CLOSE_DELAY_MS = 200;

export interface SidebarRailOptions {
  /** Where this rail's pinned choice is remembered. One key per rail. */
  storageKey: string;
  /** Shape on a browser that has never stored a choice for this rail. */
  defaultPinned: boolean;
}

/** App-shell navigation: full sidebar until the user collapses it. */
export const SHELL_SIDEBAR_RAIL: SidebarRailOptions = {
  storageKey: 'hr.shell.sidebarPinned',
  defaultPinned: true,
};

/** RFQ wizard journey: slim rail until the user pins it open. */
export const WIZARD_JOURNEY_RAIL: SidebarRailOptions = {
  storageKey: 'hr.wizard.journeyPinned',
  defaultPinned: false,
};

/** A browser that refuses storage simply starts at the caller's default. */
const readPinned = ({ storageKey, defaultPinned }: SidebarRailOptions): boolean => {
  try {
    const stored = window.localStorage.getItem(storageKey);
    return stored === null ? defaultPinned : stored === 'true';
  } catch {
    return defaultPinned;
  }
};

const writePinned = (storageKey: string, pinned: boolean) => {
  try {
    window.localStorage.setItem(storageKey, String(pinned));
  } catch {
    // Private mode / blocked storage: the preference is simply not remembered.
  }
};

export interface SidebarRailState {
  /** Rail is docked in flow — the layout makes room for it. */
  pinned: boolean;
  /** Flyout is showing over the main column (only meaningful while unpinned). */
  open: boolean;
  togglePinned: () => void;
  /** Wrap the rail + flyout; drives the outside-click and focus-out closes. */
  containerRef: React.RefObject<HTMLDivElement | null>;
  /** Focused when Escape closes the flyout, so the keyboard never lands nowhere. */
  railButtonRef: React.RefObject<HTMLButtonElement | null>;
  /** Pointer entered the rail or the flyout. */
  handleEnter: (event: React.PointerEvent) => void;
  handleLeave: () => void;
  /** Focus moved into the rail or the flyout — keyboard users get the same reveal. */
  handleFocus: () => void;
  handleBlur: (event: React.FocusEvent) => void;
}

export const useSidebarRail = (options: SidebarRailOptions): SidebarRailState => {
  const { storageKey } = options;
  const [pinned, setPinned] = useState(() => readPinned(options));
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const railButtonRef = useRef<HTMLButtonElement | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Escape hands focus back to the rail's own control, and that focus must not
  // re-open the very panel Escape just closed. Set only when the focus call
  // will actually fire an event, so a legitimate later focus still opens.
  const returningFocusRef = useRef(false);

  const clearTimer = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
  }, []);

  const scheduleOpen = useCallback(() => {
    clearTimer();
    timerRef.current = setTimeout(() => setOpen(true), OPEN_DELAY_MS);
  }, [clearTimer]);

  const scheduleClose = useCallback(() => {
    clearTimer();
    timerRef.current = setTimeout(() => setOpen(false), CLOSE_DELAY_MS);
  }, [clearTimer]);

  useEffect(() => clearTimer, [clearTimer]);

  const togglePinned = useCallback(() => {
    setPinned((was) => {
      const next = !was;
      writePinned(storageKey, next);
      return next;
    });
    clearTimer();
    setOpen(false);
  }, [clearTimer, storageKey]);

  const handleEnter = useCallback(
    (event: React.PointerEvent) => {
      // Touch taps fire pointerenter too; on a touch screen the only way in is
      // the pin button, so a tap anywhere near the rail must not open a panel
      // the user then has to tap away.
      if (pinned || event.pointerType !== 'mouse') return;
      scheduleOpen();
    },
    [pinned, scheduleOpen],
  );

  const handleLeave = useCallback(() => {
    if (pinned) return;
    scheduleClose();
  }, [pinned, scheduleClose]);

  const handleFocus = useCallback(() => {
    if (pinned) return;
    if (returningFocusRef.current) {
      returningFocusRef.current = false;
      return;
    }
    clearTimer();
    setOpen(true);
  }, [pinned, clearTimer]);

  const handleBlur = useCallback(
    (event: React.FocusEvent) => {
      if (pinned) return;
      const next = event.relatedTarget as Node | null;
      if (next && containerRef.current?.contains(next)) return;
      clearTimer();
      setOpen(false);
    },
    [pinned, clearTimer],
  );

  useEffect(() => {
    if (!open || pinned) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      const railButton = railButtonRef.current;
      returningFocusRef.current = railButton !== null && railButton !== document.activeElement;
      clearTimer();
      setOpen(false);
      railButton?.focus();
    };
    const onPointerDown = (event: MouseEvent) => {
      if (containerRef.current?.contains(event.target as Node)) return;
      clearTimer();
      setOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onPointerDown);
    };
  }, [open, pinned, clearTimer]);

  return {
    pinned,
    open: open && !pinned,
    togglePinned,
    containerRef,
    railButtonRef,
    handleEnter,
    handleLeave,
    handleFocus,
    handleBlur,
  };
};

export default useSidebarRail;
